import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { queryClient } from "App";
import MachineService from "services/Machine";

const patchStatuses = (page, machineIds, statusId, statusName) =>
  page && {
    ...page,
    data: page.data.map(machine =>
      machineIds.includes(machine.id)
        ? {
            ...machine,
            machine_approval_status_id: statusId,
            machine_approval_status: statusName,
          }
        : machine
    ),
  };

/**
 * Change the approval status on one or more machines and reflect it on the map
 * without a refetch round-trip.
 *
 * The cached page is patched up front so the sidebar and pins update as soon as
 * the analyst picks a status; a failure restores the snapshot wholesale, so a
 * partly-applied bulk change is never left on screen.
 */
export function useMachineStatusMutation(queryKey) {
  return useMutation({
    mutationFn: ({ machineIds, statusId }) =>
      MachineService.updateStatus({
        machine_ids: machineIds,
        new_status_id: statusId,
      }),

    onMutate: async ({ machineIds, statusId, statusName }) => {
      // An in-flight refetch would land after the patch and undo it.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, page =>
        patchStatuses(page, machineIds, statusId, statusName)
      );

      return { previous };
    },

    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context.previous);
      toast.error("Could not change the approval status. Nothing was saved.");
    },

    onSuccess: (_data, { machineIds, statusName }) => {
      toast.success(
        machineIds.length > 1
          ? `${machineIds.length} machines set to ${statusName}`
          : `Approval status set to ${statusName}`
      );
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
